import { theme as baseTheme, extendTheme } from '@chakra-ui/react';
import type { ComponentStyleConfig } from '@chakra-ui/theme';

const Container: ComponentStyleConfig = {
  baseStyle: {
    display: 'flex',
    background: '#f4f4f4',
    padding: 0,
    columnGap: '1px',
    boxShadow: 'rgba(0, 0, 0, 0.025) 0px -5px 20px',
    marginBottom: baseTheme.sizes[8],
    borderRadius: '20px',
    overflow: 'hidden',
  },
  variants: {
    rows: {
      flexDirection: 'row',
    },
    columns: {
      flexDirection: 'column',
    }
  },
  // The default variant value
  defaultProps: {
    variant: 'rows',
  },
};

const Panel: ComponentStyleConfig = {
  baseStyle: {
    display: 'flex',
    flex: '1',
    flexDirection: 'column',
    alignItems: 'left',
    gap: '0',
  },
  variants: {
    light: {
      background: 'white',
    },
    dark: {
      background: '#F8F9FA',
    },
  },
  // The default variant value
  defaultProps: {
    variant: 'light',
  },
};

const Button: ComponentStyleConfig = {
  baseStyle: {
    borderRadius: '20px',
    padding: '8px 16px',
  },
  variants: {
    light: {
      background: '#e5e5e5',
      color: '#000000',
      // _hover: {
      //   boxShadow: 'rgba(0, 0, 0, 0.20) 0px 5px 7px',
      // },
    },
    dark: {
      background: '#32C0A0',
      color: '#ffffff',
      // _hover: {
      //   boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 7px',
      // },
    }
  },
  sizes: {
    sm: {
      fontSize: 'sm',
    },
    md: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: 500,
    }
  },
  // The default variant value
  defaultProps: {
    variant: 'light',
    size: 'md',
  },
};

const Input: ComponentStyleConfig = {
  baseStyle: {
    field: {
      borderBottomStyle: 'solid',
      borderBottomColor: '#000000',
      borderBottomWidth: '1px',
      padding: '0 0 6px',
      transition: 'background ease-in-out 0.2s',
      background: 'linear-gradient(90deg, transparent, #1b75bb, transparent) center bottom/100% 0px no-repeat, linear-gradient(#868585, #868585) center bottom/100% 1px no-repeat',
      boxShadow: 'none',
      paddingLeft: '0',
      paddingRight: '0',
      border: 'none',
      borderBottom: '1px solid #868585',
      backgroundClip: 'content-box',
      _focus: {
        outline: 'none',
        boxShadow: '0px 2px 0px 0px #1b75bb',
        borderColor: '#1b75bb',
      },
    },
  },
  sizes: {
    unified: {
      field: {
        borderRadius: '0px',
        height: 6,
        fontSize: 'sm',
      },
    },
  },
  defaultProps: {
    variant: 'none',
    size: 'unified',
  },
};

const Select: ComponentStyleConfig = {
  baseStyle: {
    field: {
      borderBottomStyle: 'solid',
      borderBottomColor: '#000000',
      borderBottomWidth: '1px',
      padding: '0 0 6px',
      transition: 'background ease-in-out 0.2s',
      background: 'linear-gradient(90deg, transparent, #1b75bb, transparent) center bottom/100% 0px no-repeat, linear-gradient(#868585, #868585) center bottom/100% 1px no-repeat',
      boxShadow: 'none',
      paddingLeft: '0',
      paddingRight: '0',
      border: 'none',
      borderBottom: '1px solid #868585',
      backgroundClip: 'content-box',
      _focus: {
        background: 'linear-gradient(90deg, #1b75bb, #1b75bb) center bottom/100% 2px no-repeat, linear-gradient(#868585, #868585) center bottom/100% 1px no-repeat',
        outline: '0',
        borderColor: '#1b75bb',
      },
    },
  },
  sizes: {
    unified: {
      field: {
        borderRadius: '0px',
        height: 6,
        fontSize: 'sm',
      },
    },
  },
  defaultProps: {
    variant: 'none',
    size: 'unified',
  },
};


const Radio: ComponentStyleConfig = {
  baseStyle: {
    control: {
      _hover: {
        bg: 'white',
        borderColor: 'gray.900',
        color: 'gray.900',
      },
      _disabled: {
        bg: 'white',
        borderColor: 'gray.500',
        color: 'gray.500',
      },
    },
  },
  variants: {
    custom: {
      control: {
        _checked: {
          bg: 'white',
          borderColor: 'gray.900',
          color: 'gray.900',
          _hover: {
            bg: 'white',
            borderColor: 'gray.900',
            color: 'gray.900',
          },
        },
        _focus: {
          boxShadow: "none",
        },
      },
    },
  },
  defaultProps: {
    variant: 'custom',
  },
};

const Checkbox: ComponentStyleConfig = {
  baseStyle: {
    control: {
      borderColor: "gray.600",
      borderRadius: '5px',
      borderWidth: '1px',
      _hover: {
        bg: 'white',
        borderColor: 'gray.900',
        color: 'gray.900',
      },
      _disabled: {
        bg: 'white',
        borderColor: 'gray.500',
        color: 'gray.500',
      },
    },
  },
  variants: {
    custom: {
      control: {
        _checked: {
          bg: 'white',
          borderColor: 'gray.900',
          color: 'gray.900',
          _hover: {
            bg: 'white',
            borderColor: 'gray.900',
            color: 'gray.900',
          },
        },
        _focus: {
          boxShadow: "none",
        },
      },
    },
  },
  defaultProps: {
    variant: 'custom',
  },
};

const Avatar: ComponentStyleConfig = {
  baseStyle: {
    container: {
      width: '40px',
      height: '40px',
      fontSize: '18px',
      bg: '#F1F3F4',
      color: '#000000',
      fontWeight: 500,
    },
  }
};

const Alert: ComponentStyleConfig = {
  baseStyle: {
    container: {
      flexWrap: 'wrap',
      borderRadius: '10px',
      borderWidth: '1px',
      fontSize: '14px',
      lineHeight: '20px',
      padding: '16px 18px',
    },
    title: {
      fontWeight: 600,
      width: '300px',
      lineHeight: '20px',
      paddingLeft: '8px',
    },
    description: {
      paddingLeft: '28px',
      paddingTop: '4px',
      lineHeight: '20px',
    },
    icon: {
      boxSize: '16px',
    },
  },
  variants: {
    top: {
      container: {
        lineHeight: '20px',
        padding: '0px',
        borderRadius: '0px',
        borderWidth: '0px',
        bg: '#FEEFCB',
        color: '#000000',
        borderColor: '#FBC02D',
        justifyContent: 'center',
      },
      title: {
        fontWeight: 600,
        width: 'auto',
      },
      description: {
        paddingLeft: '0px',
        paddingBottom: '0px',
      },
      icon: {
        boxSize: '12px',
        color: '#7D621D',
      },
    },
    success: {
      container: {
        bg: '#EBF9F6',
        color: '#000000',
        borderColor: '#32C0A0',
      },
      icon: {
        color: '#32C0A0',
      },
    },
    warning: {
      container: {
        bg: '#FEEFCB',
        color: '#000000',
        borderColor: '#FBC02D',
      },
      icon: {
        color: '#7D621D',
      },
    },
    info: {
      container: {
        bg: '#F1F3F4',
        color: '#000000',
        borderColor: '#778295',
      },
      icon: {
        color: '#32C0A0',
      },
    },
    error: {
      container: {
        bg: '#FEEEEF',
        color: '#000000',
        borderColor: '#F3565D',
      },
      icon: {
        color: '#F3565D',
      },
    },
  },
};

export default extendTheme({
  colors: {},
  styles: {
    global: {
      body: {
        bg: '#e8e8e8',
        color: '#1e1e1e',
      },
      header: {
        borderBottom: 'solid 1px #f3f3f3',
      },
      footer: {
        borderTop: 'solid 1px #f3f3f3',
      },
    }
  },
  components: {
    Container,
    Panel,
    Button,
    Input,
    Select,
    Radio,
    Checkbox,
    Avatar,
    Alert,
  },
});


// background: linear-gradient(90deg, #1b75bb, #1b75bb) center bottom/100% 2px no-repeat, linear-gradient(#868585, #868585) center bottom/100% 1px no-repeat;
// outline: 0;
// border-color: #1b75bb;


// transition: background ease-in-out 0.2s;
// background: linear-gradient(90deg, transparent, #1b75bb, transparent) center bottom/100% 0px no-repeat, linear-gradient(#868585, #868585) center bottom/100% 1px no-repeat;
// background-clip: border-box, border-box;
// box-shadow: none;
// padding-left: 0;
// padding-right: 0;
// border: none;
// border-bottom: 1px solid #868585;
// background-clip: content-box;